import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Globe, Plus, Trash2, Edit3, Copy, Check, X, RefreshCw, AlertTriangle, ExternalLink, Loader2, ServerCog } from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  connected: { label: "Connecté", color: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "bg-emerald-500" },
  pending: { label: "En attente DNS", color: "bg-amber-100 text-amber-700 border-amber-300", dot: "bg-amber-500" },
  not_resolved: { label: "Non résolu", color: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-400" },
  mismatch: { label: "Mauvaise cible", color: "bg-red-100 text-red-700 border-red-300", dot: "bg-red-500" },
  error: { label: "Erreur", color: "bg-red-100 text-red-700 border-red-300", dot: "bg-red-500" },
};

const EMPTY = {
  domain: "",
  target_url: "",
  target_ip: "",
  client_id: "",
  site_id: "",
  registrar: "PlanetHoster",
  expires_at: "",
  notes: "",
};

export default function DomainsPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [checkingId, setCheckingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [domainsR, clientsR, sitesR] = await Promise.all([
        api.get("/agency/domains"),
        api.get("/agency/clients").catch(() => ({ data: [] })),
        api.get("/agency/sites").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(domainsR.data) ? domainsR.data : []);
      setClients(Array.isArray(clientsR.data) ? clientsR.data : []);
      // /agency/sites peut renvoyer {sites:[],count} ou [] selon les versions
      const rawSites = sitesR.data;
      const flatSites = Array.isArray(rawSites) ? rawSites : (Array.isArray(rawSites?.sites) ? rawSites.sites : []);
      setSites(flatSites);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.domain.trim() || !form.target_url.trim()) {
      toast.error("Domaine + URL preview requis");
      return;
    }
    try {
      const payload = { ...form, expires_at: form.expires_at || null };
      if (editing === "new") await api.post("/agency/domains", payload);
      else await api.put(`/agency/domains/${editing}`, payload);
      toast.success(editing === "new" ? "Domaine ajouté" : "Domaine mis à jour");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const del = async (id, domain) => {
    if (!window.confirm(`Supprimer ${domain} ?`)) return;
    await api.delete(`/agency/domains/${id}`);
    toast.success("Supprimé");
    await load();
  };

  const checkDns = async (id) => {
    setCheckingId(id);
    try {
      const { data } = await api.post(`/agency/domains/${id}/check`);
      const status = STATUS_CONFIG[data.dns_status]?.label || "Vérifié";
      if (data.dns_status === "connected") toast.success(`✅ ${data.domain} est connecté`);
      else toast.info(`Statut : ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setCheckingId(null);
    }
  };

  const copy = (text, label = "Copié") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const findClient = (cid) => clients.find(c => c.id === cid);
  const findSite = (sid) => sites.find(s => s.id === sid);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="domains-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Globe className="w-3.5 h-3.5" /> DOMAINES CLIENTS
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Gestion des domaines</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Liez chaque site COCKPIT à son nom de domaine. Pour chaque entrée, COCKPIT affiche les enregistrements DNS à copier chez votre registrar (PlanetHoster, OVH, etc.) et vérifie la connexion en temps réel.
          </p>
        </div>
        <Button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="bg-[color:var(--brand-navy,#0F2744)]" data-testid="new-domain-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un domaine
        </Button>
      </header>

      {editing && (
        <Card className="p-6 space-y-3 border-2 border-[color:var(--brand-sky,#38BDF8)]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-lg">{editing === "new" ? "Nouveau domaine client" : "Modifier le domaine"}</h2>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nom de domaine *</Label>
              <Input
                value={form.domain}
                onChange={e => setForm({ ...form, domain: e.target.value.toLowerCase().trim() })}
                placeholder="boulangerie-martin.fr"
                className="font-mono"
                data-testid="domain-input"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">Sans http:// ni www. (ex: <code>monclient.fr</code>)</p>
            </div>
            <div>
              <Label className="text-xs">URL preview du pod COCKPIT *</Label>
              <Input
                value={form.target_url}
                onChange={e => setForm({ ...form, target_url: e.target.value.trim() })}
                placeholder="https://monclient.preview.emergentagent.com"
                className="font-mono text-xs"
                data-testid="target-url-input"
              />
              <p className="text-[10px] text-slate-400 mt-0.5">URL générée par Emergent à la création du pod</p>
            </div>
          </div>
          <div>
            <Label className="text-xs">IP serveur (pour records A — optionnel mais recommandé chez PlanetHoster)</Label>
            <Input
              value={form.target_ip}
              onChange={e => setForm({ ...form, target_ip: e.target.value.trim() })}
              placeholder="Ex: 51.91.xxx.xxx"
              className="font-mono text-xs"
              data-testid="target-ip-input"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">
              Si renseigné, COCKPIT générera des <strong>records A</strong> (apex + www) en plus des CNAME — utile pour PlanetHoster qui n'accepte pas le CNAME @ racine.
              Laissez vide pour n'utiliser que des CNAME.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Client</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                value={form.client_id}
                onChange={e => setForm({ ...form, client_id: e.target.value })}
              >
                <option value="">Aucun</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Site rattaché</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                value={form.site_id}
                onChange={e => setForm({ ...form, site_id: e.target.value })}
              >
                <option value="">Aucun</option>
                {sites.filter(s => !form.client_id || s.client_id === form.client_id).map(s => (
                  <option key={s.id} value={s.id}>{s.name || s.company_name || s.domain || s.site_url || s.id}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Registrar</Label>
              <Input
                value={form.registrar}
                onChange={e => setForm({ ...form, registrar: e.target.value })}
                placeholder="PlanetHoster"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date d'expiration (optionnel)</Label>
              <Input
                type="date"
                value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
                onChange={e => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              />
            </div>
            <div>
              <Label className="text-xs">Notes internes</Label>
              <Input
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Ex: zone DNS gérée chez PlanetHoster, login N°…"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} className="bg-[color:var(--brand-navy,#0F2744)]" data-testid="domain-save-btn">
              <Check className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Globe className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500 mb-1">Aucun domaine enregistré</div>
          <div className="text-xs text-slate-400 mb-4 max-w-md mx-auto">
            Une fois votre site COCKPIT créé sur Emergent, ajoutez ici son nom de domaine final pour obtenir les DNS records à copier chez le registrar.
          </div>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="domains-list">
          {items.map(d => {
            const sc = STATUS_CONFIG[d.dns_status] || STATUS_CONFIG.pending;
            const client = findClient(d.client_id);
            const site = findSite(d.site_id);
            return (
              <Card key={d.id} className="p-5 space-y-4" data-testid={`domain-row-${d.domain}`}>
                {/* En-tête */}
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${sc.color} border`}>
                      <Globe className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a
                          href={`https://${d.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono font-bold text-slate-900 hover:text-[color:var(--brand-sky)] flex items-center gap-1"
                        >
                          {d.domain} <ExternalLink className="w-3 h-3" />
                        </a>
                        <Badge className={`${sc.color} border text-[10px] uppercase tracking-wider flex items-center gap-1`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} ${d.dns_status === "pending" ? "animate-pulse" : ""}`} />
                          {sc.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <ServerCog className="w-3 h-3" />
                        <span>Cible :</span>
                        <a href={d.target_url} target="_blank" rel="noopener noreferrer" className="font-mono hover:text-[color:var(--brand-sky)] truncate">
                          {d.target_url}
                        </a>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                        {client && <span>Client : <strong>{client.name || client.company_name}</strong></span>}
                        {site && <span>Site : <strong>{site.name || site.company_name || site.domain || site.site_url}</strong></span>}
                        {d.registrar && <span>Registrar : <strong>{d.registrar}</strong></span>}
                        {d.dns_checked_at && (
                          <span>Vérif. : {new Date(d.dns_checked_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => checkDns(d.id)}
                      disabled={checkingId === d.id}
                      data-testid={`domain-check-${d.domain}`}
                    >
                      {checkingId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      <span className="hidden sm:inline ml-1">Vérifier</span>
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditing(d.id); setForm({ ...d, expires_at: d.expires_at || "" }); }}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(d.id, d.domain)} className="text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* DNS records (toujours visibles) */}
                {Array.isArray(d.dns_records) && d.dns_records.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-600">
                        Enregistrements DNS à copier chez le registrar
                      </div>
                      {d.registrar && (
                        <span className="text-[10px] text-slate-400">→ {d.registrar}</span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                            <th className="py-1.5 pr-2">Type</th>
                            <th className="py-1.5 pr-2">Nom</th>
                            <th className="py-1.5 pr-2">Valeur (cible)</th>
                            <th className="py-1.5 pr-2">TTL</th>
                            <th className="py-1.5 pr-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.dns_records.map((r, i) => (
                            <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`dns-record-${r.type}-${r.name}`}>
                              <td className="py-2 pr-2"><Badge className="bg-blue-100 text-blue-700 border-0 font-mono text-[10px]">{r.type}</Badge></td>
                              <td className="py-2 pr-2 font-mono">{r.name}</td>
                              <td className="py-2 pr-2 font-mono text-slate-700 break-all">{r.value}</td>
                              <td className="py-2 pr-2 text-slate-500">{r.ttl}</td>
                              <td className="py-2 pr-2">
                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copy(r.value, `${r.type} ${r.name} copié`)}>
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {d.dns_records.some(r => r.note) && (
                      <ul className="mt-2 space-y-1 text-[11px] text-slate-500">
                        {d.dns_records.filter(r => r.note).map((r, i) => (
                          <li key={i}>· <strong>{r.name}</strong> : {r.note}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Erreur DNS */}
                {d.dns_error && d.dns_status !== "connected" && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800">
                      <div className="font-bold mb-0.5">DNS non opérationnel</div>
                      <div>{d.dns_error}</div>
                      {d.dns_resolved_to && (
                        <div className="mt-1">Résolution actuelle : <code className="font-mono bg-white px-1 rounded">{d.dns_resolved_to}</code></div>
                      )}
                      <div className="mt-1 text-[10px] opacity-75">⏱ La propagation DNS peut prendre jusqu'à 24h après la modification chez le registrar.</div>
                    </div>
                  </div>
                )}

                {d.notes && (
                  <div className="text-[11px] text-slate-400 italic">{d.notes}</div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
